import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'
import { useRouteMatch } from 'react-router'
import { useTranslate } from 'react-admin'
import QueryKeys from '../utils/constants/queryKeys'
import getEventTypes from '../Apis/event/getEventTypes'
import createEvent from '../Apis/event/createEvent'
import getEvent from '../Apis/event/getEvent'
import deleteEvent from '../Apis/event/deleteEvent'
import updateEvent from '../Apis/event/updateEvent'
import getTeams from '../Apis/team/getTeams'
import getGroupTeams from '../Apis/groups/getGroupTeams'
import getEvents from '../Apis/event/getEvents'
import parseEvents from '../utils/helpers/eventHelpers/parseEvents'
import useQueryParams from '../hooks/useQueryParams'
import useHistoryPush from '../hooks/useHistoryPush'
import getCourts from '../Apis/courts/getCourts'
import useGetLocalStorage from '../hooks/useGetLocalStorage'
import localStorageIds from '../utils/constants/localStorageIds'
import { EVENT_TYPE_FORMS } from '../utils/constants/eventTypeForms'
import filterTeamsByPermission from '../utils/helpers/filterTeamsByPermission'
import usePrevious from '../hooks/usePrevious'
import orgIsPayLocked from '../utils/helpers/orgIsPayLocked'
import ConnectionErrorPage from '../pages/ErrorInConnection/ConnectionErrorPage'
import USER_PERMISSIONS from '../utils/constants/userPermissions'

const TeamCalendarContext = createContext()

const TeamCalendarProvider = ({ children, teamsSource = 'organization', requireSeason = false }) => {
  const [createEventDrawerOpen, setCreateEventDrawerOpen] = useState(false)
  const [selectedEventType, setSelectedEventType] = useState()
  const [dates, setDates] = useState()
  const [dateClick, setDateClick] = useState()
  const [events, setEvents] = useState()
  const [coordinatesModal, setCoordinatesModal] = useState({ x: 0, y: 0 })
  const [formDirty, setFormDirty] = useState()
  const [gamedayId, setGamedayId] = useState(null)
  const eventId = useRouteMatch('/calendar/:id')?.params?.id
  const eventTeamId = useRouteMatch('/teamCalendar/:id')?.params?.id
  const [clickedEventId, setClickedEventId] = useState(eventId || eventTeamId || '')
  // Another org's event (a federation game a paired club reads) is read under that org's route.
  const [clickedEventOrgId, setClickedEventOrgId] = useState(null)
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const queryParams = useQueryParams()
  const { org, team } = queryParams
  const localStorage = useGetLocalStorage()
  const selectedSeason = localStorage?.[localStorageIds.SELECTED_SEASON]
  const groupId = localStorage?.[localStorageIds.SELECTED_GROUP]?.id || null
  const stageId = localStorage?.[localStorageIds.SELECTED_STAGE]?.id || null
  const { redirect, pathname } = useHistoryPush()

  useEffect(() => {
    if (eventId || eventTeamId) {
      setCreateEventDrawerOpen(true)
    }
  }, [eventId, eventTeamId])

  const getAllEvents = useQuery(
    [QueryKeys.GET_EVENTS, dates, org, team, stageId, groupId, selectedSeason?.id],
    () =>
      getEvents({
        org,
        ...(team && { teams: team }),
        ...(stageId && { stageId }),
        ...(groupId && { groupId }),
        ...(selectedSeason?.id && { seasonId: selectedSeason.id }),
        startDateTime: dates?.start.toISOString(),
        endDateTime: dates?.end.toISOString(),
      }),
    {
      retry: 4,
      // The CMS pages seed a three-year window only so the game popover can resolve its event;
      // without a season that asks for every event of the org (a federation's ~10,000, seconds on
      // the server), so they wait until a season is chosen. The calendar shows all competitions.
      enabled: Boolean(dates?.start) && (!requireSeason || Boolean(selectedSeason?.id)),
      onSuccess: (resp) => {
        setEvents(resp)
      },
    },
  )

  const event = useQuery(
    [QueryKeys.GET_EVENT, clickedEventId, clickedEventOrgId, org],
    () =>
      // Another org's event (a paired federation game) under that org, the calendar's own under its org.
      getEvent({ orgId: clickedEventOrgId || org, eventId: clickedEventId }),
    {
      // Another org's event is not in this org's list, so it does not wait for it.
      enabled: Boolean(clickedEventId && (getAllEvents?.data || clickedEventOrgId)),
      // Seed with the entry from the already-loaded calendar list so the modal shows
      // title/date/teams instantly instead of rendering empty (and "Invalid Date")
      // while the detail fetch is in flight. Detail-only fields fill in on resolve.
      placeholderData: () =>
        Array.isArray(getAllEvents?.data)
          ? getAllEvents.data.find((e) => e.id === clickedEventId)
          : undefined,
    },
  )

  const eventTypes = useQuery([QueryKeys.GET_EVENT_TYPES, org], () => getEventTypes(org), {
    enabled: !!org,
  })

  // Derived via effect rather than onSuccess, which doesn't run when a mount
  // is served from fresh cache and would leave selectedEventType unset.
  useEffect(() => {
    const response = eventTypes.data
    if (!response) return
    if (queryParams?.eventType) {
      setSelectedEventType(response.find((type) => type.code === queryParams?.eventType))
      return
    }

    setSelectedEventType(response?.find((type) => type.code === EVENT_TYPE_FORMS.GAME))
  }, [eventTypes.data, queryParams?.eventType])

  // Conditionally fetch teams based on URL params
  // If groupId exists in URL, fetch group teams; otherwise fetch organization teams
  const teams = useQuery(
    groupId && teamsSource === 'season'
      ? [QueryKeys.GET_GROUP_TEAMS, org, groupId]
      : [QueryKeys.GET_TEAMS, org || null],
    () => {
      // If groupId exists in URL and we're in CMS mode, fetch group teams
      if (groupId && teamsSource === 'season' && org) {
        return getGroupTeams(org, groupId)
      }
      // Otherwise, fetch organization teams (default)
      return org && getTeams(org)
    },
    {
      enabled:
        groupId && teamsSource === 'season'
          ? Boolean(org && groupId)
          : Boolean(org),
    }
  )

  // Errors are surfaced globally by the axios interceptor (ErrorNotificationHandler),
  // which shows the backend's `details`/`message`. No per-mutation notify — that produced
  // a second, generic "Error 400" toast on top of the meaningful one.
  const createAnEvent = useMutation((data) => createEvent({ ...data, orgId: org }), {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.GET_EVENTS)
      queryClient.invalidateQueries(QueryKeys.GET_STAGES) // Refresh CMS data
      setCreateEventDrawerOpen(false)
      setGamedayId(null) // Clear gamedayId after successful creation
      redirect(pathname, [], ['breadcrumb', 'eventType'])
    },
  })

  const deleteAnEvent = useMutation((data) => {
    // Support both old format (just eventId) and new format (object with eventTypeCode and eventId)
    const orgId = clickedEventOrgId || org
    if (typeof data === 'object' && data.eventTypeCode && data.eventId) {
      return deleteEvent({ ...data, orgId })
    }
    // Legacy support: if just an eventId string is passed, get type from events
    const eventToDelete = getAllEvents?.data?.find((e) => e.id === data)
    if (eventToDelete?.type?.code) {
      return deleteEvent({ eventTypeCode: eventToDelete.type.code, eventId: data, orgId })
    }
    // Fallback if event not found in list
    throw new Error('Event not found or missing type code')
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.GET_EVENTS)
      queryClient.invalidateQueries(QueryKeys.GET_STAGES) // Refresh CMS data
      setCreateEventDrawerOpen(false)
    },
  })

  const updateAnEvent = useMutation((data) => updateEvent({ ...data, orgId: clickedEventOrgId || org }), {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.GET_EVENTS)
      queryClient.invalidateQueries(QueryKeys.GET_EVENT)
      queryClient.invalidateQueries(QueryKeys.GET_RUNNING_BROADCASTS_EVENTS)
      queryClient.invalidateQueries(QueryKeys.GET_STAGES) // Refresh CMS data
      setCreateEventDrawerOpen(false)
      redirect(`/${pathname.split('/')[1]}`, [], ['eventType'])
    },
  })

  const isUpdatingPrevious = usePrevious(updateAnEvent?.isLoading)

  // After update an event, clear clickedEventId
  useEffect(() => {
    if (isUpdatingPrevious && !updateAnEvent?.isLoading) {
      setClickedEventId('')
    }
  }, [updateAnEvent?.isLoading, isUpdatingPrevious])

  const courtsQuery = useInfiniteQuery(
    [QueryKeys.GET_COURTS, org],
    ({ pageParam = 1 }) => getCourts(org, pageParam),
    {
      enabled: Boolean(org),
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce((sum, p) => sum + (p.courts?.length ?? 0), 0)
        if (lastPage.totalCount != null) {
          return fetched < lastPage.totalCount ? allPages.length + 1 : undefined
        }
        return (lastPage.courts?.length ?? 0) >= 40 ? allPages.length + 1 : undefined
      },
    },
  )

  useEffect(() => {
    if (courtsQuery.hasNextPage && !courtsQuery.isFetchingNextPage) {
      courtsQuery.fetchNextPage()
    }
  }, [courtsQuery.hasNextPage, courtsQuery.isFetchingNextPage, courtsQuery.fetchNextPage])

  const allCourts = useMemo(
    () => courtsQuery.data?.pages?.flatMap((p) => p.courts ?? []) ?? [],
    [courtsQuery.data],
  )

  const currentTeam =
    teams && teams.data && teams.data.length > 0 && teams.data.find((t) => t.id === team)

  const EVENT_TYPES_FILTER = useMemo(
    () => [
      {
        // Event types request is not finished or failed
        condition: () => !eventTypes.data,
        action: () => {
          return []
        },
      },
      {
        // Organisation cannot use what it is subscribed to until it pays
        condition: () => orgIsPayLocked(),
        action: () => {
          return eventTypes.data.slice(0, 2)
        },
      },
      {
        // Otherwise, everything gone when
        condition: () => true,
        action: () => {
          return eventTypes.data
        },
      },
    ],
    [eventTypes.data],
  )

  const value = useMemo(
    () => ({
      // Wrap the value object creation in useMemo
      createEventDrawerOpen,
      setCreateEventDrawerOpen,
      selectedEventType,
      setSelectedEventType,
      clickedEventId,
      setClickedEventId,
      clickedEventOrgId,
      setClickedEventOrgId,
      coordinatesModal,
      setCoordinatesModal,
      formDirty,
      setFormDirty,
      gamedayId,
      setGamedayId,
      translate,
      currentTeam,
      event: event?.data,
      // The placeholder below is the calendar-list entry: enough for the click modal, but it
      // carries no court and no broadcast, so anything form-shaped has to wait for the detail.
      eventIsPlaceholder: Boolean(event?.isPlaceholderData),
      // If organisation is on Inactive subscription state, the user can only see what the livestream addon provides
      eventTypes: EVENT_TYPES_FILTER.find((rule) => rule.condition()).action(),
      createEvent: createAnEvent,
      deleteEvent: deleteAnEvent,
      updateEvent: updateAnEvent,
      // If organisation is on Inactive subscription state, the user can only see what the livestream addon provides
      events: orgIsPayLocked()
        ? parseEvents(
            events?.filter((ev) => ev?.type.code === 'game' || ev?.type.code === 'pract'),
            team,
          )
        : parseEvents(events, team),
      eventsRaw: events,
      setDates,
      dateClick,
      setDateClick,
      setEvents,
      teams: !teams.isLoading ? filterTeamsByPermission(teams?.data, USER_PERMISSIONS.CREATE_EVENT) : [],
      eventLoading: getAllEvents.isFetching,
      courts: allCourts,
    }),
    [
      createEventDrawerOpen,
      selectedEventType,
      clickedEventId,
      clickedEventOrgId,
      coordinatesModal,
      formDirty,
      gamedayId,
      translate,
      currentTeam,
      event?.data,
      event?.isPlaceholderData,
      EVENT_TYPES_FILTER,
      createAnEvent,
      deleteAnEvent,
      updateAnEvent,
      events,
      team,
      dateClick,
      teams.isLoading,
      teams?.data,
      getAllEvents.isFetching,
      allCourts,
    ],
  )

  return (
    <TeamCalendarContext.Provider value={value}>
      {getAllEvents.isError ? <ConnectionErrorPage /> : children}
    </TeamCalendarContext.Provider>
  )
}
TeamCalendarProvider.propTypes = {
  children: PropTypes.node.isRequired,
  teamsSource: PropTypes.oneOf(['organization', 'season']),
  requireSeason: PropTypes.bool,
}

const useTeamCalendarContext = () => {
  const context = useContext(TeamCalendarContext)
  if (context === undefined) {
    throw new Error('useTeamCalendarContext must be used within a TeamCalendarProvider')
  }
  return context
}

export { TeamCalendarProvider, useTeamCalendarContext }
